import {
  RETENTION_PERIODS,
  periodCovering,
  periodDays,
  periodLabel,
} from '@hookpipe/contracts/retention/retention-period';
import { calendarDay, sizeText } from '../../shared/lib/format';
import type {
  TFlowLevel,
  TRetentionKeeps,
  TRetentionPeriod,
  TRetentionUsage,
  TRetentionVolume,
  TRetryWarning,
} from './api';

export { RETENTION_PERIODS, periodLabel, periodDays };

export const formatBytes = sizeText;

const DAY_SEC = 86_400;

// "21 d" when it is days, "18 h" under one: a warning must not round a retry window to 0 d.
export const spanWords = (seconds: number): string =>
  seconds >= DAY_SEC
    ? `${Math.ceil(seconds / DAY_SEC).toLocaleString('en-US')} d`
    : `${Math.max(1, Math.ceil(seconds / 3_600))} h`;

export const warningText = (warning: TRetryWarning, keeps: TRetentionPeriod): string => {
  const who = warning.stepName ?? 'Its longest step';
  const how = warning.retries ? 'retries' : 'holds copies';
  return `${who} ${how} for up to ${spanWords(warning.seconds)} — with ${periodLabel(keeps)}, a copy that gives up can’t be redelivered.`;
};

export const periodOfDays = (days: number): TRetentionPeriod => periodCovering(days);

export const coversRetries = (period: TRetentionPeriod, seconds: number | null): boolean => {
  const days = periodDays(period);
  return days === null || seconds === null || seconds <= days * DAY_SEC;
};

export const folderSourceWords = (keeps: TRetentionKeeps, own: TRetentionPeriod | null): string =>
  own !== null ? 'set here' : keeps.source === 'folder' ? `from ${keeps.folderName}` : 'default';

export const flowSourceWords = (flow: Pick<TFlowLevel, 'ownPeriod' | 'keeps'>): string =>
  flow.ownPeriod !== null
    ? 'this flow'
    : flow.keeps.source === 'folder'
      ? 'from folder'
      : 'default';

export const inheritsWords = (keeps: TRetentionKeeps): string =>
  keeps.source === 'folder' && keeps.folderName ? keeps.folderName : 'the default';

export interface IStoredKind {
  key: string;
  title: string;
  note: string;
  bytes: number;
  rows: number;
  unit: string;
}

const pick = (usage: TRetentionUsage, ...types: TRetentionVolume['type'][]) =>
  usage.types.filter((volume) => types.includes(volume.type));

const sum = (volumes: TRetentionVolume[], key: 'bytes' | 'rows') =>
  volumes.reduce((total, volume) => total + volume[key], 0);

export const storedKinds = (usage: TRetentionUsage): IStoredKind[] => {
  const rows: [string, string, string, TRetentionVolume['type'][], string][] = [
    [
      'requests',
      'Webhook bodies and headers',
      'the requests as they arrived',
      ['request_content'],
      'requests',
    ],
    ['responses', 'Responses', 'what destinations answered', ['responses'], 'responses'],
    [
      'steps',
      'Step results',
      'what each step produced',
      ['step_outputs', 'transform_output'],
      'results',
    ],
    [
      'history',
      'History',
      'which runs happened, where each copy went, and how it ended. Content follows each flow’s period; history is kept at least 90 d, longer if the flow keeps longer, and Forever keeps both.',
      ['requests', 'events_attempts', 'runs_steps'],
      'records',
    ],
  ];
  return rows.map(([key, title, note, types, unit]) => {
    const volumes = pick(usage, ...types);
    return { key, title, note, unit, bytes: sum(volumes, 'bytes'), rows: sum(volumes, 'rows') };
  });
};

export const OLDER_DATA = 'Older data (before flows)';

export const ownerName = (owner: { kind: string; name: string | null }): string =>
  owner.kind === 'older' ? OLDER_DATA : (owner.name ?? 'A deleted flow');

export const ownersText = (owners: readonly { kind: string }[]): string => {
  const flows = owners.filter((owner) => owner.kind === 'flow').length;
  const older = owners.some((owner) => owner.kind === 'older');
  if (flows === 0) return older ? 'older data' : 'no flow';
  const named = `${flows.toLocaleString('en-US')} ${flows === 1 ? 'flow' : 'flows'}`;
  return older ? `${named} and older data` : named;
};

// "since 12 Sep" (tracked or rebuilt days) until 30 d; one lone point is where tracking began.
export const growthText = (usage: TRetentionUsage, now: number): string => {
  const bytes = usage.growth30dBytes;
  const size = `${bytes < 0 ? '−' : '+'}${sizeText(Math.abs(bytes))}`;
  if (!usage.growthSince) return `${size} in the last 30 days`;
  const since = calendarDay(`${usage.growthSince}T12:00:00Z`, now, 'UTC');
  return usage.daily.length >= 2
    ? `${size} since ${since}`
    : `${size} since tracking started ${since}`;
};

export const keptBytes = (usage: TRetentionUsage): number =>
  sum(pick(usage, 'issues', 'bookmarks'), 'bytes');

export const daysToQuota = (usage: TRetentionUsage, now: number): number | null => {
  const tracked = usage.growthSince
    ? Math.max(1, (now - Date.parse(`${usage.growthSince}T00:00:00Z`)) / (DAY_SEC * 1000))
    : 30;
  const perDay = usage.growth30dBytes / tracked;
  if (perDay <= 0) return null;
  return Math.max(0, (usage.quotaBytes - usage.totalBytes) / perDay);
};

// The quota is set in whole gigabytes of 2^30 bytes: the column's default is 214748364800,
// which people know as "200 GB". Decimal would print 214.7 and round-trip the dialog to 215.
export const QUOTA_GB = 1024 ** 3;

export const quotaText = (bytes: number): string => {
  const gigabytes = bytes / QUOTA_GB;
  return `${Number.isInteger(gigabytes) ? gigabytes.toLocaleString('en-US') : gigabytes.toFixed(1)} GB`;
};

/** "33.9 kB of 200 GB": each side in its own unit, so a small part never reads as 0.0. */
export const ofText = (part: number, whole: number): string =>
  `${sizeText(part)} of ${quotaText(whole)}`;

export const isRetentionPeriod = (value: string): value is TRetentionPeriod =>
  (RETENTION_PERIODS as readonly string[]).includes(value);
